import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Key, Copy, Check, RefreshCw, Eye, EyeOff, AlertTriangle, Shield, User,
  Code, Terminal, ChevronDown, ChevronUp, Activity, Coins, Clock,
  BarChart2, Zap, Info, ExternalLink, FileCode, Play, Ruler
} from 'lucide-react';
import { apiKeysAPI } from '../../api';
import toast from 'react-hot-toast';
import { format, parseISO } from 'date-fns';

// Stats Card Component
const StatsCard = ({ icon: Icon, title, value, subtitle, className = '' }) => (
  <div className={`bg-white rounded-xl border border-slate-200 p-2.5 sm:p-4 ${className}`}>
    <div className="flex items-center gap-2">
      <div className="w-7 h-7 sm:w-10 sm:h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
        <Icon className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-slate-600" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] sm:text-xs text-slate-500 truncate">{title}</p>
        <p className="text-base sm:text-xl font-bold text-slate-900">{value}</p>
        {subtitle && <p className="text-[10px] sm:text-xs text-slate-400 truncate">{subtitle}</p>}
      </div>
    </div>
  </div>
);

// Code Block Component with Copy
const CodeBlock = ({ title, code, language = 'bash' }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied to clipboard!');
  };

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden">
      <div className="flex items-center justify-between px-3 sm:px-4 py-2 bg-slate-100 border-b border-slate-200">
        <div className="flex items-center gap-2 min-w-0">
          <Terminal className="w-4 h-4 text-slate-500 flex-shrink-0" />
          <span className="text-xs sm:text-sm font-medium text-slate-700 truncate">{title}</span>
        </div>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 sm:gap-1.5 px-2 py-1 text-xs text-slate-600 hover:bg-slate-200 rounded transition-colors flex-shrink-0"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
          <span className="hidden sm:inline">{copied ? 'Copied!' : 'Copy'}</span>
        </button>
      </div>
      <div className="overflow-x-auto">
        <pre className="p-3 sm:p-4 text-[10px] sm:text-sm bg-slate-900 text-slate-100 whitespace-pre-wrap break-all sm:whitespace-pre sm:break-normal">
          <code>{code}</code>
        </pre>
      </div>
    </div>
  );
};

// Response Preview Component
const ResponsePreview = ({ title, response }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden mt-3">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 sm:px-4 py-2 bg-green-50 hover:bg-green-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Play className="w-4 h-4 text-green-600" />
          <span className="text-xs sm:text-sm font-medium text-green-700">{title}</span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-green-600" /> : <ChevronDown className="w-4 h-4 text-green-600" />}
      </button>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="overflow-x-auto">
              <pre className="p-3 sm:p-4 text-[10px] sm:text-xs bg-slate-50 text-slate-700 border-t border-slate-200 whitespace-pre-wrap break-all sm:whitespace-pre sm:break-normal">
                <code>{JSON.stringify(response, null, 2)}</code>
              </pre>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const APIKeys = () => {
  const [apiKeyData, setApiKeyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [copiedPartnerId, setCopiedPartnerId] = useState(false);
  const [activeTab, setActiveTab] = useState('tryon');

  // Get user info from localStorage
  const userStr = localStorage.getItem('nanokart_user');
  const user = userStr ? JSON.parse(userStr) : {};
  const partnerShortId = user?.partner_short_id || '';
  const partnerId = user?.partner_id || '';
  const displayId = partnerShortId || partnerId;

  useEffect(() => {
    fetchKeyData();
  }, []);

  const fetchKeyData = async () => {
    try {
      const response = await apiKeysAPI.get();
      setApiKeyData(response.data);
    } catch (error) {
      toast.error('Failed to load API key data');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const response = await apiKeysAPI.regenerate();
      setNewKey(response.data.api_key);
      toast.success('API key regenerated successfully!');
      fetchKeyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate key');
    } finally {
      setRegenerating(false);
      setShowConfirm(false);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(''), 2000);
    toast.success('Copied to clipboard!');
  };

  const copyPartnerId = () => {
    if (displayId) {
      navigator.clipboard.writeText(displayId);
      setCopiedPartnerId(true);
      setTimeout(() => setCopiedPartnerId(false), 2000);
      toast.success('Partner ID copied!');
    }
  };

  const maskApiKey = (key) => {
    if (!key) return '••••••••••••••••••••••••••••••••';
    return key.substring(0, 8) + '••••••••••••••••••••' + key.substring(key.length - 4);
  };

  // Format usage stats
  const formatCredits = (num) => {
    if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
    return num?.toString() || '0';
  };

  // API Examples
  const baseUrl = 'https://api.nanokart.ai/api/v1';
  const apiKey = apiKeyData?.api_key || 'YOUR_API_KEY';
  
  const tryonExample = `curl -X POST "${baseUrl}/tryon/external" \\
  -H "X-API-Key: ${showKey ? apiKey : 'YOUR_API_KEY'}" \\
  -H "X-Partner-Id: ${displayId}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "human_image_url": "https://example.com/model.jpg",
    "garment_image_url": "https://example.com/garment.jpg",
    "sku_id": "SKU001",
    "customer_id": "customer_123"
  }'`;

  const tryonResponse = {
    "request_id": "550e8400-e29b-41d4-a716-446655440000",
    "status": "success",
    "tryon_key": "tryon/tryon:smart:v1:abc123def456.bin",
    "image_url": "https://cdn.nanokart.ai/results/tryon_abc123.jpg",
    "cached": false,
    "credits_consumed": 1,
    "latency_ms": 2340
  };

  const momentsExample = `curl -X POST "${baseUrl}/moments/external" \\
  -H "X-API-Key: ${showKey ? apiKey : 'YOUR_API_KEY'}" \\
  -H "X-Partner-Id: ${displayId}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "tryon_key": "tryon/tryon:smart:v1:abc123def456.bin",
    "moment_type": "creative_workday",
    "custom_prompt": "Professional office setting",
    "sku_id": "SKU001",
    "customer_id": "customer_123"
  }'`;

  const momentsResponse = {
    "request_id": "660e8400-e29b-41d4-a716-446655440001",
    "status": "success",
    "image_url": "https://cdn.nanokart.ai/results/moments_xyz789.jpg",
    "moment_type": "creative_workday",
    "credits_consumed": 2,
    "latency_ms": 3120
  };

  const momentTypes = [
    { value: 'creative_workday', label: 'Creative Workday', description: 'Professional office/work setting' },
    { value: 'unwind', label: 'Unwind', description: 'Relaxed, casual home setting' },
    { value: 'after_hours', label: 'After Hours', description: 'Evening social/party setting' },
    { value: 'escape', label: 'The Escape', description: 'Outdoor/travel/vacation setting' },
    { value: 'gentle_celebration', label: 'Gentle Celebration', description: 'Elegant celebration setting' }
  ];

  const sizeRecommendExample = `curl -X POST "${baseUrl}/size/recommend" \\
  -H "X-API-Key: ${showKey ? apiKey : 'YOUR_API_KEY'}" \\
  -H "X-Partner-Id: ${displayId}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "image_s3_key": "tryon/tryon:smart:v1:abc123def456.bin",
    "height_cm": 165,
    "product_id": "prod_xyz789",
    "sku_id": "SKU001",
    "customer_id": "customer_123"
  }'`;

  const sizeRecommendWithChartExample = `curl -X POST "${baseUrl}/size/recommend" \\
  -H "X-API-Key: ${showKey ? apiKey : 'YOUR_API_KEY'}" \\
  -H "X-Partner-Id: ${displayId}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "image_s3_key": "tryon/tryon:smart:v1:abc123def456.bin",
    "height_cm": 165,
    "size_chart_cm": {
      "XS": { "chest_cm": 84, "waist_cm": 68, "hip_cm": 90 },
      "S":  { "chest_cm": 88, "waist_cm": 72, "hip_cm": 94 },
      "M":  { "chest_cm": 92, "waist_cm": 76, "hip_cm": 98 },
      "L":  { "chest_cm": 96, "waist_cm": 80, "hip_cm": 102 },
      "XL": { "chest_cm": 100, "waist_cm": 84, "hip_cm": 106 }
    }
  }'`;

  const sizeChartFetchExample = `curl -X GET "${baseUrl}/inventory/prod_xyz789/size-chart" \\
  -H "X-API-Key: ${showKey ? apiKey : 'YOUR_API_KEY'}" \\
  -H "X-Partner-Id: ${displayId}"`;

  const sizeRecommendResponse = {
    "request_id": "990e8400-e29b-41d4-a716-446655440004",
    "status": "success",
    "recommended_size": "M",
    "confidence_percent": 84,
    "fit_notes": "M fits well across chest and waist. Slight room at hips.",
    "photo_sufficient": true,
    "insufficiency_message": null,
    "engine": "v2",
    "cached": false,
    "credits_consumed": 2,
    "latency_ms": 980
  };

  const sizeChartResponse = {
    "product_id": "prod_xyz789",
    "has_size_chart": true,
    "size_chart_cm": {
      "XS": { "chest_cm": 84, "waist_cm": 68, "hip_cm": 90 },
      "S":  { "chest_cm": 88, "waist_cm": 72, "hip_cm": 94 },
      "M":  { "chest_cm": 92, "waist_cm": 76, "hip_cm": 98 },
      "L":  { "chest_cm": 96, "waist_cm": 80, "hip_cm": 102 },
      "XL": { "chest_cm": 100, "waist_cm": 84, "hip_cm": 106 }
    },
    "gender": "female"
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 max-w-5xl mx-auto overflow-x-hidden" data-testid="api-keys-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">API Keys</h1>
          <p className="text-slate-500 text-sm">Manage your API authentication and view usage</p>
        </div>
        <button
          onClick={() => setShowConfirm(true)}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          <span className="hidden sm:inline">Regenerate Key</span>
          <span className="sm:hidden">Regenerate</span>
        </button>
      </div>

      {/* New Key Alert */}
      <AnimatePresence>
        {newKey && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 sm:p-5"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Key className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-emerald-800 mb-1">New API Key Generated!</h3>
                <p className="text-emerald-700 text-sm mb-3">
                  Copy your new API key now. For security, it won&apos;t be shown again in full.
                </p>
                <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-emerald-200">
                  <code className="flex-1 text-emerald-700 text-sm break-all font-mono">{newKey}</code>
                  <button
                    onClick={() => copyToClipboard(newKey, 'new')}
                    className="p-2 hover:bg-emerald-50 rounded flex-shrink-0"
                  >
                    {copied === 'new' ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-slate-500" />}
                  </button>
                </div>
              </div>
            </div>
            <button
              onClick={() => setNewKey(null)}
              className="mt-3 text-sm text-emerald-600 hover:text-emerald-700"
            >
              Dismiss
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Credentials Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Partner ID Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 sm:p-5 border border-blue-100"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <User className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-blue-700 font-medium">Partner ID</p>
              <p className="text-xs text-blue-500">Required in X-Partner-Id header</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-blue-200">
            <code className="flex-1 text-lg font-mono font-bold text-slate-800" data-testid="partner-id-display">
              {displayId}
            </code>
            <button
              onClick={copyPartnerId}
              className="p-2 hover:bg-blue-50 rounded transition-colors"
              title="Copy Partner ID"
              data-testid="copy-partner-id-btn"
            >
              {copiedPartnerId ? (
                <Check className="w-4 h-4 text-green-600" />
              ) : (
                <Copy className="w-4 h-4 text-slate-500" />
              )}
            </button>
          </div>
        </motion.div>

        {/* API Key Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 sm:p-5 border border-purple-100"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Key className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-purple-700 font-medium">API Key</p>
                <p className="text-xs text-purple-500">Required in X-API-Key header</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {apiKeyData?.version && (
                <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                  v{apiKeyData.version}
                </span>
              )}
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                apiKeyData?.status === 'active' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-red-100 text-red-700'
              }`}>
                {apiKeyData?.status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-purple-200">
            <code className="flex-1 text-sm font-mono text-slate-700 truncate" data-testid="api-key-display">
              {showKey ? apiKeyData?.api_key : maskApiKey(apiKeyData?.api_key)}
            </code>
            <button
              onClick={() => setShowKey(!showKey)}
              className="p-2 hover:bg-purple-50 rounded transition-colors"
              title={showKey ? 'Hide Key' : 'Show Key'}
            >
              {showKey ? <EyeOff className="w-4 h-4 text-slate-500" /> : <Eye className="w-4 h-4 text-slate-500" />}
            </button>
            <button
              onClick={() => copyToClipboard(apiKeyData?.api_key, 'key')}
              className="p-2 hover:bg-purple-50 rounded transition-colors"
              title="Copy API Key"
            >
              {copied === 'key' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-slate-500" />}
            </button>
          </div>
          <p className="text-xs text-purple-500 mt-2">
            Created: {apiKeyData?.created_at ? format(parseISO(apiKeyData.created_at), 'MMM d, yyyy h:mm a') : '-'}
          </p>
        </motion.div>
      </div>

      {/* Usage Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
      >
        <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <Activity className="w-4 h-4" />
          API Key Usage (This Month)
        </h3>
        <div className="grid grid-cols-2 gap-2 sm:gap-3 sm:grid-cols-4">
          <StatsCard
            icon={Zap}
            title="API Calls"
            value={formatCredits(apiKeyData?.usage?.total_calls || 0)}
            subtitle="Total requests"
          />
          <StatsCard
            icon={Coins}
            title="Credits Used"
            value={formatCredits(apiKeyData?.usage?.credits_consumed || 0)}
            subtitle="Via API"
          />
          <StatsCard
            icon={BarChart2}
            title="TryOn Calls"
            value={formatCredits(apiKeyData?.usage?.tryon_calls || 0)}
            subtitle={`${formatCredits(apiKeyData?.usage?.tryon_credits || 0)} credits`}
          />
          <StatsCard
            icon={BarChart2}
            title="Moments Calls"
            value={formatCredits(apiKeyData?.usage?.moments_calls || 0)}
            subtitle={`${formatCredits(apiKeyData?.usage?.moments_credits || 0)} credits`}
          />
        </div>
        {apiKeyData?.last_used && (
          <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Last used: {format(parseISO(apiKeyData.last_used), 'MMM d, yyyy h:mm a')}
          </p>
        )}
      </motion.div>

      {/* Security Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-amber-50 border border-amber-200 rounded-xl p-4 sm:p-5"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Shield className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h3 className="font-semibold text-amber-800 mb-2">Security Best Practices</h3>
            <ul className="text-amber-700 text-sm space-y-1">
              <li>• Never expose your API key in client-side code or public repositories</li>
              <li>• Store keys in environment variables or secure vaults</li>
              <li>• Regenerate immediately if you suspect the key is compromised</li>
              <li>• Monitor usage regularly for unexpected activity</li>
            </ul>
          </div>
        </div>
      </motion.div>

      {/* API Documentation */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="bg-white rounded-xl border border-slate-200 overflow-hidden"
      >
        <div className="p-4 sm:p-5 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Code className="w-5 h-5 text-slate-600" />
              <h3 className="font-semibold text-slate-900">API Usage Examples</h3>
            </div>
            <a
              href="https://docs.nanokart.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              Full Docs <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Examples are pre-filled with your Partner ID. Toggle &quot;Show Key&quot; above to include your actual API key.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-slate-200 overflow-x-auto">
          <button
            onClick={() => setActiveTab('tryon')}
            className={`px-4 sm:px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'tryon'
                ? 'border-blue-600 text-blue-600 bg-blue-50'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Virtual Try-On API
          </button>
          <button
            onClick={() => setActiveTab('moments')}
            className={`px-4 sm:px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'moments'
                ? 'border-blue-600 text-blue-600 bg-blue-50'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Moments API
          </button>
          <button
            onClick={() => setActiveTab('size')}
            className={`px-4 sm:px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${
              activeTab === 'size'
                ? 'border-emerald-600 text-emerald-600 bg-emerald-50'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid="size-api-tab"
          >
            <Ruler className="w-3.5 h-3.5" />
            Size API
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-4 sm:p-5">
          {activeTab === 'tryon' && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-700">
                  <p className="font-medium">POST /api/v1/tryon/external</p>
                  <p className="text-blue-600">Generate virtual try-on images by combining human and garment photos.</p>
                </div>
              </div>
              
              <CodeBlock
                title="cURL Request"
                code={tryonExample}
              />
              
              <ResponsePreview
                title="Example Success Response"
                response={tryonResponse}
              />

              <div className="bg-slate-50 rounded-lg p-4 text-sm">
                <h4 className="font-medium text-slate-700 mb-2">Request Parameters:</h4>
                <ul className="space-y-1 text-slate-600">
                  <li><code className="bg-slate-200 px-1 rounded">human_image_url</code> (required) - URL of the human/model image</li>
                  <li><code className="bg-slate-200 px-1 rounded">garment_image_url</code> (required) - URL of the garment image</li>
                  <li><code className="bg-slate-200 px-1 rounded">sku_id</code> (optional) - SKU ID for tracking</li>
                  <li><code className="bg-slate-200 px-1 rounded">customer_id</code> (optional) - Customer ID for analytics</li>
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'moments' && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg border border-purple-100">
                <Info className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-purple-700">
                  <p className="font-medium">POST /api/v1/moments/external</p>
                  <p className="text-purple-600">Transform try-on results into lifestyle scenes using AI.</p>
                </div>
              </div>
              
              <CodeBlock
                title="cURL Request"
                code={momentsExample}
              />
              
              <ResponsePreview
                title="Example Success Response"
                response={momentsResponse}
              />

              <div className="bg-slate-50 rounded-lg p-4 text-sm">
                <h4 className="font-medium text-slate-700 mb-2">Request Parameters:</h4>
                <ul className="space-y-1 text-slate-600">
                  <li><code className="bg-slate-200 px-1 rounded">tryon_key</code> (required) - tryon_key from a previous TryOn result</li>
                  <li><code className="bg-slate-200 px-1 rounded">moment_type</code> (required) - One of the lifestyle moment types</li>
                  <li><code className="bg-slate-200 px-1 rounded">custom_prompt</code> (optional) - Additional scene description (max 500 chars)</li>
                  <li><code className="bg-slate-200 px-1 rounded">sku_id</code> (optional) - SKU ID for tracking</li>
                  <li><code className="bg-slate-200 px-1 rounded">customer_id</code> (optional) - Customer ID for analytics</li>
                </ul>
              </div>

              <div className="bg-purple-50 rounded-lg p-4">
                <h4 className="font-medium text-purple-800 mb-2">Available Moment Types:</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {momentTypes.map((moment) => (
                    <div key={moment.value} className="flex items-start gap-2 p-2 bg-white rounded border border-purple-100">
                      <code className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-mono">{moment.value}</code>
                      <span className="text-xs text-slate-600">{moment.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'size' && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                <Ruler className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-emerald-700">
                  <p className="font-medium">POST /api/v1/size/recommend</p>
                  <p className="text-emerald-600">Get AI-powered size recommendation using a customer's body from their try-on image and your product size chart.</p>
                </div>
              </div>

              {/* Flow steps */}
              <div className="bg-blue-50 rounded-lg p-4 text-sm border border-blue-100">
                <h4 className="font-medium text-blue-800 mb-2">How it works:</h4>
                <ol className="space-y-1.5 text-blue-700">
                  <li className="flex items-start gap-2">
                    <span className="w-4 h-4 rounded-full bg-blue-600 text-white flex items-center justify-center text-[10px] flex-shrink-0 mt-0.5">1</span>
                    Run a TryOn and save the <code className="bg-blue-100 px-1 rounded">tryon_key</code> from the response
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-4 h-4 rounded-full bg-blue-600 text-white flex items-center justify-center text-[10px] flex-shrink-0 mt-0.5">2</span>
                    Pass <code className="bg-blue-100 px-1 rounded">tryon_key</code> as <code className="bg-blue-100 px-1 rounded">image_s3_key</code> + customer height + size chart
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-4 h-4 rounded-full bg-blue-600 text-white flex items-center justify-center text-[10px] flex-shrink-0 mt-0.5">3</span>
                    Receive recommended size, confidence %, and natural language fit notes
                  </li>
                </ol>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 text-sm">
                <h4 className="font-medium text-slate-700 mb-2">Request Parameters:</h4>
                <ul className="space-y-1.5 text-slate-600">
                  <li><code className="bg-slate-200 px-1 rounded">image_s3_key</code> <span className="text-red-500">(required)</span>  -  The <code className="bg-slate-200 px-1 rounded">tryon_key</code> from TryOn response</li>
                  <li><code className="bg-slate-200 px-1 rounded">height_cm</code> <span className="text-red-500">(required)</span>  -  Customer height in cm (50–250)</li>
                  <li><code className="bg-slate-200 px-1 rounded">product_id</code> <span className="text-amber-600">(conditional)</span>  -  Auto-fetches size chart from your inventory</li>
                  <li><code className="bg-slate-200 px-1 rounded">size_chart_cm</code> <span className="text-amber-600">(conditional)</span>  -  Raw size chart JSON if not using product_id</li>
                  <li><code className="bg-slate-200 px-1 rounded">sku_id</code>, <code className="bg-slate-200 px-1 rounded">customer_id</code>  -  Optional, for analytics</li>
                </ul>
                <p className="text-xs text-slate-500 mt-2">Either <code className="bg-slate-200 px-1 rounded">product_id</code> or <code className="bg-slate-200 px-1 rounded">size_chart_cm</code> is required.</p>
              </div>

              <CodeBlock title="cURL  -  Using product_id (recommended)" code={sizeRecommendExample} />
              <ResponsePreview title="Example Success Response" response={sizeRecommendResponse} />

              <CodeBlock title="cURL  -  Using size_chart_cm directly" code={sizeRecommendWithChartExample} />

              {/* Size chart format */}
              <div className="bg-emerald-50 rounded-lg p-4">
                <h4 className="font-medium text-emerald-800 mb-2">Response Fields:</h4>
                <ul className="space-y-1 text-emerald-800 text-sm">
                  <li><code className="bg-emerald-100 px-1 rounded">recommended_size</code>  -  e.g. "M", "L", "42", "S/M"</li>
                  <li><code className="bg-emerald-100 px-1 rounded">confidence_percent</code>  -  0–100 fit confidence score</li>
                  <li><code className="bg-emerald-100 px-1 rounded">fit_notes</code>  -  Human-readable fit advice</li>
                  <li><code className="bg-emerald-100 px-1 rounded">photo_sufficient</code>  -  false if body was partially visible</li>
                  <li><code className="bg-emerald-100 px-1 rounded">insufficiency_message</code>  -  Shown when confidence is low</li>
                </ul>
              </div>

              {/* Fetch size chart */}
              <div className="border-t border-slate-200 pt-4">
                <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100 mb-3">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium">GET /api/v1/inventory/&#123;product_id&#125;/size-chart</p>
                    <p className="text-blue-600">Fetch the size chart stored for a product in your Nanokart inventory.</p>
                  </div>
                </div>
                <CodeBlock title="Fetch Product Size Chart" code={sizeChartFetchExample} />
                <ResponsePreview title="Example Size Chart Response" response={sizeChartResponse} />
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Quick Reference */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl p-4 sm:p-5 border border-slate-200"
      >
        <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <FileCode className="w-5 h-5 text-slate-600" />
          Quick Reference
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div className="bg-white rounded-lg p-3 border border-slate-200 overflow-hidden">
            <p className="text-slate-500 text-xs mb-1">Base URL</p>
            <code className="text-slate-700 font-mono text-xs break-all">https://api.nanokart.ai/api/v1</code>
          </div>
          <div className="bg-white rounded-lg p-3 border border-slate-200">
            <p className="text-slate-500 text-xs mb-1">Required Headers</p>
            <code className="text-slate-700 font-mono text-xs">X-API-Key, X-Partner-Id</code>
          </div>
          <div className="bg-white rounded-lg p-3 border border-slate-200">
            <p className="text-slate-500 text-xs mb-1">TryOn Endpoint</p>
            <code className="text-slate-700 font-mono text-xs">POST /tryon/external</code>
          </div>
          <div className="bg-white rounded-lg p-3 border border-slate-200">
            <p className="text-slate-500 text-xs mb-1">Moments Endpoint</p>
            <code className="text-slate-700 font-mono text-xs">POST /moments/external</code>
          </div>
          <div className="bg-white rounded-lg p-3 border border-emerald-200">
            <p className="text-slate-500 text-xs mb-1">Size Recommendation</p>
            <code className="text-emerald-700 font-mono text-xs">POST /size/recommend</code>
          </div>
          <div className="bg-white rounded-lg p-3 border border-slate-200">
            <p className="text-slate-500 text-xs mb-1">Fetch Size Chart</p>
            <code className="text-slate-700 font-mono text-xs">GET /inventory/&#123;id&#125;/size-chart</code>
          </div>
        </div>
      </motion.div>

      {/* Regenerate Confirmation Modal */}
      <AnimatePresence>
        {showConfirm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Regenerate API Key?</h2>
                  <p className="text-sm text-slate-500">This action cannot be undone</p>
                </div>
              </div>
              
              <div className="bg-amber-50 rounded-lg p-3 mb-4 text-sm text-amber-800">
                <p>⚠️ Your current API key will be <strong>immediately deactivated</strong>. Any applications using it will stop working.</p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirm(false)}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-slate-700 font-medium hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRegenerate}
                  disabled={regenerating}
                  className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {regenerating ? (
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      Regenerate
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default APIKeys;
